import { useQuery } from '@tanstack/react-query';
import { Scale, AlertTriangle, CheckCircle, DollarSign, Loader2, Users, PieChart, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

const fmtCurrency = (v: number) => `$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const RISK_STYLES: Record<string, { label: string; variant: 'default' | 'destructive' | 'secondary' | 'outline'; color: string }> = {
  low: { label: 'Low Risk', variant: 'default', color: 'text-green-500' },
  medium: { label: 'Medium Risk', variant: 'secondary', color: 'text-amber-500' },
  high: { label: 'High Risk', variant: 'destructive', color: 'text-red-500' },
  no_data: { label: 'No Data', variant: 'outline', color: 'text-muted-foreground' },
};

export default function TrustDistributionPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['trust-distribution'],
    queryFn: async () => { const r = await apiClient.get('/compliance/trust-distribution'); return r.data.data; },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Analysing trust distributions...</p>
        </div>
      </div>
    );
  }

  const summary = data?.summary;
  const clients = data?.clients || [];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Scale}
        title="Trust Distribution Planner"
        description="Distribution scenario modelling with tax optimisation for trust entities"
      />


      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Users className="h-5 w-5 text-primary" /></div>
                <div><p className="text-2xl font-bold">{summary.totalTrusts}</p><p className="text-xs text-muted-foreground">Trusts Analysed</p></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center"><DollarSign className="h-5 w-5 text-green-500" /></div>
                <div><p className="text-2xl font-bold">{fmtCurrency(summary.totalDistributableIncome)}</p><p className="text-xs text-muted-foreground">Total Distributable Income</p></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center"><PieChart className="h-5 w-5 text-amber-500" /></div>
                <div><p className="text-2xl font-bold">{fmtCurrency(summary.avgTaxSavings)}</p><p className="text-xs text-muted-foreground">Avg Tax Savings (Optimised)</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className={summary.complianceIssues > 0 ? 'border-red-500/30' : ''}>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-red-500" /></div>
                <div><p className="text-2xl font-bold">{summary.complianceIssues}</p><p className="text-xs text-muted-foreground">Compliance Flags</p></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client Results */}
      {clients.filter((c: any) => c.status !== 'no_data').length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No trust entities with accounting data found. Connect clients with trust structures to accounting software.
          </CardContent>
        </Card>
      ) : (
        clients.filter((c: any) => c.status !== 'no_data').map((c: any) => {
          const style = RISK_STYLES[c.riskLevel] || RISK_STYLES.no_data;
          return (
            <Card key={c.clientId}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{c.clientName}</CardTitle>
                    <CardDescription>Distributable income: {fmtCurrency(c.distributableIncome)}</CardDescription>
                  </div>
                  <Badge variant={style.variant}>{style.label}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Distribution Scenarios */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {(c.scenarios || []).map((s: any) => (
                    <div key={s.name} className={cn(
                      'border rounded-lg p-3',
                      s.name === 'Tax-Optimised' ? 'border-primary/30 bg-primary/5' : ''
                    )}>
                      <p className="text-xs font-medium mb-2 flex items-center gap-1">
                        {s.name === 'Tax-Optimised' && <CheckCircle className="h-3 w-3 text-primary" />}
                        {s.name}
                      </p>
                      <p className="text-lg font-bold">{fmtCurrency(s.totalTax)}</p>
                      <p className="text-[10px] text-muted-foreground">Estimated total tax</p>
                      {s.savings > 0 && (
                        <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                          <TrendingDown className="h-3 w-3" />
                          Save {fmtCurrency(s.savings)} vs equal split
                        </p>
                      )}
                      <div className="mt-2 space-y-1">
                        {(s.beneficiaries || []).map((b: any, i: number) => (
                          <div key={i} className="flex justify-between text-[11px]">
                            <span className="text-muted-foreground">{b.name}</span>
                            <span className="font-mono">{fmtCurrency(b.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Compliance Flags */}
                {c.complianceFlags && c.complianceFlags.length > 0 && (
                  <div className="border-t pt-3">
                    <p className="text-xs font-medium text-red-500 mb-2 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Compliance Flags
                    </p>
                    <ul className="space-y-1.5">
                      {c.complianceFlags.map((f: any, i: number) => {
                        // The API returns objects {type, severity, description,
                        // recommendation}; tolerate plain strings too.
                        const text = typeof f === 'string' ? f : (f?.description || f?.type || '');
                        const rec = typeof f === 'object' && f ? f.recommendation : undefined;
                        const sev = typeof f === 'object' && f ? f.severity : undefined;
                        const dash = sev === 'high' ? 'text-red-500' : sev === 'medium' ? 'text-amber-500' : 'text-red-400';
                        return (
                          <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                            <span className={cn('mt-0.5', dash)}>-</span>
                            <span>
                              {text}
                              {rec && (
                                <span className="block text-[11px] text-muted-foreground/70 mt-0.5">Recommendation: {rec}</span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}
